import tokenManager from './tokenManager';
import { logger } from './logger';

/**
 * 行動イベント計測 (BI) ユーティリティ。
 *
 * POST /api/analytics/track に送る。未ログインの LP からも呼べる（認証任意）。
 * 失敗は握り潰して UX に一切影響させない（console/logger のみ）。
 *
 * 識別子:
 * - anonymous_id: localStorage 永続 UUID（未ログインでも訪問を追跡・signup 後のスティッチ用）
 * - session_id: sessionStorage の UUID（タブ/セッション単位）
 * - 初回タッチ (utm_* / referrer / landing_page): 初回訪問時に localStorage へ保存し以降引き継ぐ
 *
 * サーバー側で Sanctum トークンから user_id / workspace_id を確定するため、
 * ここでは user_id / workspace_id を送らない（なりすまし防止）。
 */

/**
 * event_name の定数。バックエンド ProductAnalyticsTracker::EVENT_NAMES と完全一致させること
 * （表記揺れ禁止・ホワイトリスト外は 422）。フロントで発火する分をここに定義する。
 */
export const ANALYTICS_EVENTS = {
  LP_VIEWED: 'lp_viewed',
  PRICING_VIEWED: 'pricing_viewed',
  CTA_CLICKED: 'cta_clicked',
  SIGNUP_STARTED: 'signup_started',
  SIGNUP_COMPLETED: 'signup_completed',
  DOWNLOAD_COMPLETED: 'download_completed',
} as const;

export type AnalyticsEventName = (typeof ANALYTICS_EVENTS)[keyof typeof ANALYTICS_EVENTS];

const ANON_ID_KEY = 'tasky_anonymous_id';
const SESSION_ID_KEY = 'tasky_analytics_session';
const FIRST_TOUCH_KEY = 'tasky_first_touch';
const ONCE_KEY_PREFIX = 'tasky_analytics_once:';

const isBrowser = (): boolean => typeof window !== 'undefined' && typeof document !== 'undefined';

const createUuid = (): string => {
  if (isBrowser() && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const rand = Math.floor(Math.random() * 16);
    const value = char === 'x' ? rand : (rand & 0x3) | 0x8;
    return value.toString(16);
  });
};

const safeLocalGet = (key: string): string | null => {
  if (!isBrowser()) return null;
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const safeLocalSet = (key: string, value: string): void => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(key, value);
  } catch {
    // localStorage が使えない環境では無視する
  }
};

export const getAnonymousId = (): string => {
  const existing = safeLocalGet(ANON_ID_KEY);
  if (existing) return existing;
  const anonId = createUuid();
  safeLocalSet(ANON_ID_KEY, anonId);
  return anonId;
};

export const getSessionId = (): string => {
  if (!isBrowser()) return createUuid();
  try {
    let sessionId = window.sessionStorage.getItem(SESSION_ID_KEY);
    if (!sessionId) {
      sessionId = createUuid();
      window.sessionStorage.setItem(SESSION_ID_KEY, sessionId);
    }
    return sessionId;
  } catch {
    return createUuid();
  }
};

interface FirstTouch {
  source?: string;
  medium?: string;
  campaign?: string;
  content?: string;
  term?: string;
  landing_page?: string;
  referrer?: string;
}

/**
 * 初回タッチの流入情報を捕捉する。初回のみ URL の utm_* / referrer / landing_page を
 * 保存し、以降はそれを引き継ぐ（後続イベントにも同じ初回タッチを付ける）。
 */
const captureFirstTouch = (): FirstTouch => {
  const existing = safeLocalGet(FIRST_TOUCH_KEY);
  if (existing) {
    try {
      return JSON.parse(existing) as FirstTouch;
    } catch {
      // 壊れていたら捕捉し直す
    }
  }

  if (!isBrowser()) return {};

  const params = new URLSearchParams(window.location.search);
  const firstTouch: FirstTouch = {
    source: params.get('utm_source') || undefined,
    medium: params.get('utm_medium') || undefined,
    campaign: params.get('utm_campaign') || undefined,
    content: params.get('utm_content') || undefined,
    term: params.get('utm_term') || undefined,
    landing_page: window.location.href,
    referrer: document.referrer || undefined,
  };

  safeLocalSet(FIRST_TOUCH_KEY, JSON.stringify(firstTouch));
  return firstTouch;
};

const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
const baseURL = apiUrl.endsWith('/api') ? apiUrl : `${apiUrl}/api`;

/**
 * 行動イベントを送信する（fire-and-forget）。
 *
 * @param eventName  バックエンドのホワイトリスト内の event_name
 * @param properties イベント固有の追加情報（cta_id / type 等）。PII は入れない。
 */
export const track = (eventName: AnalyticsEventName, properties?: Record<string, unknown>): void => {
  try {
    if (!isBrowser()) return;

    const firstTouch = captureFirstTouch();
    const body = {
      event_name: eventName,
      event_id: createUuid(),
      event_timestamp: new Date().toISOString(),
      anonymous_id: getAnonymousId(),
      session_id: getSessionId(),
      landing_page: firstTouch.landing_page,
      referrer: firstTouch.referrer,
      source: firstTouch.source,
      medium: firstTouch.medium,
      campaign: firstTouch.campaign,
      content: firstTouch.content,
      term: firstTouch.term,
      properties: properties && Object.keys(properties).length > 0 ? properties : undefined,
    };

    const headers: Record<string, string> = { 'Content-Type': 'application/json' };
    const authToken = tokenManager.getToken();
    if (authToken) {
      headers['Authorization'] = `Bearer ${authToken}`;
    }

    // keepalive: ページ遷移中でも送信を完了させる。失敗は握り潰す。
    fetch(`${baseURL}/analytics/track`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
      keepalive: true,
    }).catch((error) => {
      logger.error('productAnalytics track failed', error);
    });
  } catch (error) {
    logger.error('productAnalytics track error', error);
  }
};

/**
 * セッション内で 1 回だけ送る track。signup_started / signup_completed など、
 * 再レンダリングや StrictMode の二重マウントでの重複を抑止したいイベント用。
 */
export const trackOnce = (eventName: AnalyticsEventName, properties?: Record<string, unknown>): void => {
  if (!isBrowser()) return;

  const onceKey = `${ONCE_KEY_PREFIX}${eventName}`;
  try {
    if (window.sessionStorage.getItem(onceKey) === '1') return;
    window.sessionStorage.setItem(onceKey, '1');
  } catch {
    // sessionStorage が使えない環境ではそのまま送信する
  }

  track(eventName, properties);
};

export default { track, trackOnce, ANALYTICS_EVENTS };
